import { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import {
  Alert,
  TextInput,
  Text,
  Paper,
  Group,
  Button,
  PasswordInput,
  Anchor,
  Stack,
  Container,
  Center,
  Loader,
  Divider,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { fetchInvite, userConfirmation } from "./authApi";
import { useStore } from "../store";
import { Link, useLocation } from "wouter";
import { socialLoginFactory } from "./socialLogin";
import { GoogleButton } from "../_Shared/GoogleButton";

export default function InvitationForm({ params, appName, hideGoogle, ...props }) {
  const { t } = useTranslation("Auth");
  const [invitation, setInvitation] = useState(null);
  // null | "invitation_not_found" | "invitation_expired" | "invitation_accepted"
  const [inviteProblem, setInviteProblem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const form = useForm({
    initialValues: {
      password: "",
      emailAddress: "",
      terms: false,
      invitationToken: "",
    },
    validate: {
      password: (val) => (val.length < 8 ? t("invalid_password") : null),
    },
  });

  const authToken = useStore((state) => state.authToken);
  const userLoggedIn = useStore((state) => state.userLoggedIn);
  const [, setLocation] = useLocation();

  const socialLogin = socialLoginFactory((event) => {
    userLoggedIn(event.data);
  });

  useEffect(() => {
    if (authToken) setLocation("/");
  }, [authToken]);

  // An unusable invitation answers 404 (unknown) or 410 (expired / accepted, see error.code).
  const problemFrom = (err) =>
    err?.code === "invitation_expired" || err?.code === "invitation_accepted"
      ? err.code
      : "invitation_not_found";

  useEffect(() => {
    fetchInvite(params.token)
      .then((data) => {
        if (!data?.token) {
          setInviteProblem("invitation_not_found");
          return;
        }
        form.setFieldValue("invitationToken", data.token);
        form.setFieldValue("emailAddress", data.emailAddress);
        setInvitation(data);
      })
      .catch((err) => setInviteProblem(problemFrom(err)));
  }, [params.token]);

  if (inviteProblem) {
    const texts = {
      invitation_not_found: [
        t("invitation_not_found_title", "Invitation not found"),
        t(
          "invitation_not_found_message",
          "This invitation link is not valid. Ask the person who invited you to send a new invitation. If you already accepted it, log in instead.",
        ),
      ],
      invitation_expired: [
        t("invitation_expired_title", "Invitation expired"),
        t(
          "invitation_expired_message",
          "This invitation is no longer valid. Invitations are valid for 48 hours. Ask the person who invited you to send a new one.",
        ),
      ],
      invitation_accepted: [
        t("invitation_accepted_title", "Invitation already accepted"),
        t(
          "invitation_accepted_message",
          "This invitation was already used. Log in with your email address and password.",
        ),
      ],
    };
    const [title, message] = texts[inviteProblem];
    return (
      <Container size="xs" mt="xl">
        <Paper radius="md" p="xl" withBorder {...props}>
          <Text size="lg" fw={500} mb="md">
            {title}
          </Text>
          <Text>{message}</Text>
          <Group justify="flex-end" mt="xl">
            <Button component={Link} href="/auth/login" radius="xl" variant="light">
              {t("login")}
            </Button>
          </Group>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="xs" mt="xl">
      {errorMessage && (
        <Alert
          variant="light"
          color="red"
          title={t("invitation_failed", "Could not accept the invitation")}
          mb="lg"
          icon={<IconInfoCircle />}
        >
          {errorMessage}
        </Alert>
      )}

      <Paper radius="md" p="xl" withBorder {...props}>
        {!invitation && (
          <Center>
            <Loader size="xl" mt="xl" />
          </Center>
        )}

        {invitation && (
          <Text my="md">
            {t("invitation_intro", {
              inviter: invitation.inviterName,
              appName,
              defaultValue:
                "{{inviter}} invited you to join their workspace on {{appName}}. Set a password to accept.",
            })}
          </Text>
        )}

        {!hideGoogle && (
          <Group grow mb="md" mt="md">
            <GoogleButton
              onClick={() => socialLogin("Google", params.token)}
              radius="xl"
            >
              Google
            </GoogleButton>
          </Group>
        )}

        {!hideGoogle && (
          <Divider
            label={t("or_set_password", "Or set a password to log in with email")}
            labelPosition="center"
            my="lg"
          />
        )}

        <form
          onSubmit={form.onSubmit((values) => {
            setIsLoading(true);
            setErrorMessage(null);
            userConfirmation(values)
              .then((data) => userLoggedIn(data))
              .catch((err) => {
                if (err.status === 404 || err.status === 410) {
                  setInviteProblem(problemFrom(err));
                  return;
                }
                setErrorMessage(
                  err.status === 429
                    ? t(
                        "activation_rate_limited",
                        "Too many attempts. Please wait a minute and try again.",
                      )
                    : t(
                        "invitation_error",
                        "This did not work. Please try again in a moment.",
                      ),
                );
              })
              .finally(() => setIsLoading(false));
          })}
        >
          <Stack>
            <TextInput
              label={t("email")}
              disabled
              value={form.values.emailAddress}
              radius="md"
            />

            <PasswordInput
              required
              disabled={isLoading}
              autoComplete="new-password"
              label={t("password")}
              placeholder={t("choose_password", "Choose a password (at least 8 characters)")}
              value={form.values.password}
              onChange={(event) =>
                form.setFieldValue("password", event.currentTarget.value)
              }
              error={form.errors.password}
              radius="md"
            />
          </Stack>

          <Group justify="space-between" mt="xl">
            <Anchor href="/auth/login" component={Link} c="dimmed" size="xs">
              {t("have_account_login", "Already have an account? Log in")}
            </Anchor>
            <Button
              loading={isLoading}
              disabled={!invitation}
              type="submit"
              radius="xl"
            >
              {t("accept_invitation", "Accept invitation")}
            </Button>
          </Group>
        </form>
      </Paper>
    </Container>
  );
}
